import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import React, { useEffect, useState } from "react";
import { cpfValidation } from "../../../PsichologistManagement/components/CpfInputField";
import {
  CrpInputField,
  validateCpr,
} from "../../../PsichologistManagement/components/CrpInputField";

export interface PromoteClientModalDelegate {
  onConfirmPromotion: (cpf: string, crp: string) => void;
  onClosePromotion: () => void;
}

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  clientName: string;
  delegate: PromoteClientModalDelegate;
  isToClient?: boolean;
}

export function PromoteClientModal({
  isOpen,
  isLoading,
  clientName,
  delegate,
  isToClient = true,
}: Props) {
  const [cpf, setCpf] = useState("");
  const [crp, setCrp] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCpf("");
      setCrp("");
    }
  }, [isOpen]);

  const formatCpf = (digits: string): string => {
    let v = digits;
    if (v.length > 3) v = v.slice(0, 3) + "." + v.slice(3);
    if (v.length > 7) v = v.slice(0, 7) + "." + v.slice(7);
    if (v.length > 11) v = v.slice(0, 11) + "-" + v.slice(11);
    return v.slice(0, 14);
  };

  const handleCpfChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const digits = e.target.value.replace(/\D/g, "");
    const masked = formatCpf(digits);
    setCpf(masked);
  };

  const handleConfirm = () => {
    const isCpfValid = cpfValidation(cpf);
    const { isValid: isCrpValid } = validateCpr(crp);

    if (isCpfValid && isCrpValid) {
      delegate.onConfirmPromotion(cpf, crp);
    }
  };

  const isCpfValid = cpfValidation(cpf);
  const { isValid: isCrpValid } = validateCpr(crp);

  const showCpfError = cpf.length > 0 && !isCpfValid;
  const showCrpError = crp.length > 0 && !isCrpValid;
  const showCrpEmptyError = crp.length > 0 && !isCrpValid;

  const isFormValid =
    isCpfValid && isCrpValid && cpf.length > 0 && crp.length > 0;

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (!isLoading) delegate.onClosePromotion();
      }}
      disableEscapeKeyDown={isLoading}
      hideBackdrop={false}
      variant="modal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle variant="modal">
        <AssignmentIndIcon />
        Promover a Psicólogo
      </DialogTitle>
      <DialogContent variant="modal">
        {isLoading ? (
          <Box
            sx={{
              my: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>
              Validando dados automaticamente, por favor aguarde...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary">
              Para iniciar a promoção de <strong>{clientName}</strong>, insira
              os dados abaixo:
            </Typography>

            <TextField
              fullWidth
              required
              label={isToClient ? 'CPF do Cliente':  'CPF do Usuário'}
              variant="outlined"
              value={cpf}
              onChange={handleCpfChange}
              error={showCpfError}
              helperText={
                showCpfError ? "Formato de CPF inválido (XXX.XXX.XXX-XX)" : ""
              }
              inputProps={{ maxLength: 14 }}
              sx={{ mt: 3, mb: 1.5 }}
            />

            <CrpInputField
              required
              label={isToClient ? 'CRP do Cliente':  'CRP do Usuário'}
              value={crp}
              onChange={setCrp}
              error={showCrpError || showCrpEmptyError}
              helperText={
                showCrpEmptyError
                  ? "O CRP é obrigatório"
                  : showCrpError
                  ? "Formato de CRP inválido (XX/YYYYY com região entre 01 e 24)"
                  : ""
              }
            />

            {(showCpfError || showCrpError || showCrpEmptyError) && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {showCpfError && (
                  <>
                    Por favor, informe um CPF válido. Ex: 123.456.789-00
                    <br />
                  </>
                )}
                {showCrpEmptyError && (
                  <>
                    Por favor, informe o CRP do {isToClient? 'Cliente' : 'Usuário'}.
                    <br />
                  </>
                )}
                {showCrpError && (
                  <>
                    Por favor, use XX/YYYYY com região entre 01 e 24. Ex:
                    06/66501
                  </>
                )}
              </Alert>
            )}

          </>
        )}
      </DialogContent>
      {!isLoading && (
        <DialogActions
          variant="modal"
          sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={delegate.onClosePromotion}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
          >
            Enviar Solicitação
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
